import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import ListingInactivityWarning from '../components/common/ListingInactivityWarning';
import {
  ListingProvider,
  ListingResource,
  useListingActions,
  useListingField,
  useListingStatus,
  useListingStore,
  useMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useService } from './ServiceContext';
import { useMetadata } from './MetadataContext';
import BackHandlerContent from '../components/common/BackHandlerContent';
import { navigateTo } from '../utils';
import { KEYSTONE_REDIRECT_URL, NGW_REDIRECT_URL } from '../config';
import { useModal } from './ModalContext';
import { useTenantPath } from '../hooks/useTenantPath';
import { PropertyField } from '../system/field';
import { ApplicationEvent, useAnalytics } from './AnalyticsContext';
import { useSidePanel } from './SidePanelContext';
import PublishConfirmation from '../components/common/PublishConfirmation';
import { MediaCategory } from '../system/media';
import ListingSummary from '../components/common/ListingSummary';
import PublishError from '../components/common/PublishError';
import { useAuth } from './AuthContext';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNotification } from './hooks/useNotification';
import { MediaSyncDialog } from '../components/edit/media/MediaSyncDialog';
import { useMediaSync } from '../components/edit/media/hooks/useMediaSync';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';

interface EditListingContextType {
  exit: (hasErrors: boolean, isFromLiveListing: boolean) => void;
  publish: () => void;
}

const EditListingContext = createContext<EditListingContextType | undefined>(
  undefined,
);

export const EditListingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { saveListing, publishListing, discardListing } = useListingActions();
  const { showModal, hideModal } = useModal();
  const { showPanel, hidePanel } = useSidePanel();
  const { captureEvent } = useAnalytics();
  const { sendSellingAgentEmail } = useNotification();
  const { getLiveValues, registerMediaSyncErrorHandler } = useService();

  const isNavigatingAway = useListingStatus<boolean>('isNavigatingAway', false);
  const isPublishing = useListingStatus<boolean>('isPublishing', false);

  const listingKey = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const listingId = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_ID,
    '',
  );
  const mlsStatus = useListingField(
    ListingResource.Property,
    PropertyField.MLS_STATUS,
    '',
  );
  const isListingOE = useListingField(
    ListingResource.Property,
    PropertyField.OFFICE_EXCLUSIVE_YN,
    false,
  );
  const propertyType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const listPrice = useListingField(
    ListingResource.Property,
    PropertyField.LIST_PRICE,
    0,
  );
  const address = useListingField(
    ListingResource.Property,
    PropertyField.UNPARSED_ADDRESS,
    '',
  );
  const sellingAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.BUYER_AGENT_MLS_ID,
    '',
  );
  const coSellingAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.CO_BUYER_AGENT_MLS_ID,
    '',
  );

  const getLiveListingValues = async () => {
    try {
      if (!listingKey.value) {
        return;
      }
      return await getLiveValues(listingKey.value, true, [
        PropertyField.BUYER_AGENT_MLS_ID,
        PropertyField.CO_BUYER_AGENT_MLS_ID,
      ]);
    } catch (error) {
      console.error('Error getting live listing values', error);
      throw error;
    }
  };

  useHotkeys(
    'ctrl+m',
    () => navigateTo(`${KEYSTONE_REDIRECT_URL}#Edit:${listingKey.value}`, true),
    [listingKey.value],
  );

  // Get media count using the media hook
  const { count: mediaCount } = useMediaResource();

  // Check if media sync error handling feature is enabled
  const { hasFeature } = useFeatureFlags();
  const isMediaSyncEnabled = hasFeature(FeatureFlags.NGW_MEDIA_FAILURE_MODAL);
  const { getPath } = useTenantPath();

  // Get the appropriate overview URL based on feature flag
  const getOverviewUrl = useCallback(() => {
    const showOverview = hasFeature(FeatureFlags.NGW_OVERVIEW);
    return showOverview ? getPath(NGW_REDIRECT_URL) : KEYSTONE_REDIRECT_URL;
  }, [hasFeature, getPath]);

  // Media sync error handling
  const [showMediaSyncDialog, setShowMediaSyncDialog] = useState(false);

  const { syncMedia, isSyncing: isMediaSyncing } = useMediaSync({
    listingKey: listingKey.value,
    onSyncComplete: () => {
      setShowMediaSyncDialog(false);
    },
    onSyncError: (error) => {
      console.error('Failed to sync media from EditListingContext:', error);
    },
  });

  // Register handler with ServiceContext only if feature is enabled
  useEffect(() => {
    if (isMediaSyncEnabled) {
      registerMediaSyncErrorHandler?.((error: Error) => {
        console.error('Media sync error from saveListing:', error);
        setShowMediaSyncDialog(true);
      });
    }
  }, [registerMediaSyncErrorHandler, isMediaSyncEnabled]);

  const maybeSendSellingAgentEmail = async (
    isSellingAgentUpdated: boolean,
    isCoSellingAgentUpdated: boolean,
  ) => {
    // QUESTION: Can we get these from a pre-existing type, or should we make an enum?
    const acceptedStatuses = [
      'ACTIVE UNDER CONTRACT-BRIGHT',
      'PENDING-BRIGHT',
      'CLOSED-BRIGHT',
    ];

    const agentHasChanged = isSellingAgentUpdated || isCoSellingAgentUpdated;

    // set up Buyer and/or CoBuyer mlsIds as email recipients
    const mlsIds = [];
    if (isSellingAgentUpdated && sellingAgentMlsId.value) {
      mlsIds.push(sellingAgentMlsId.value);
    }
    if (
      isCoSellingAgentUpdated &&
      coSellingAgentMlsId.value &&
      sellingAgentMlsId.value !== coSellingAgentMlsId.value
    ) {
      mlsIds.push(coSellingAgentMlsId.value);
    }

    // Conditions for sending the Selling Agent Email
    // * Listing is Not OE
    // * Listing has status "Active Under Contract", "Pending", or "Closed"
    // * Buyer and/or Co-Buyer Agent are present on the listing
    // ** AND Buyer and/or Co-Buyer Agent has changed since last publish
    const shouldSendSellingAgentEmail =
      !isListingOE.value &&
      acceptedStatuses.includes(mlsStatus.value) &&
      (sellingAgentMlsId.value || coSellingAgentMlsId.value) &&
      agentHasChanged;

    if (shouldSendSellingAgentEmail) {
      sendSellingAgentEmail(listingId.value, mlsIds);
    }
  };

  const publish = async () => {
    // Inform autosave and other operations that publish is in progress
    isPublishing.updateValue(true);

    const submit = async (loading: (isLoading: boolean) => void) => {
      loading(true);

      try {
        // get current listing values before publishing over them
        // we're checking if one or more of the selling agents has changed,
        // to decide if we should send the selling agent email notification
        const liveValues = await getLiveListingValues();
        const isSellingAgentUpdated =
          sellingAgentMlsId.value.length > 0 &&
          sellingAgentMlsId.value !==
            liveValues?.Property?.BUYER_AGENT_MLS_ID &&
          sellingAgentMlsId.value !==
            liveValues?.Property?.CO_BUYER_AGENT_MLS_ID;
        const isCoSellingAgentUpdated =
          coSellingAgentMlsId.value.length > 0 &&
          coSellingAgentMlsId.value !==
            liveValues?.Property?.BUYER_AGENT_MLS_ID &&
          coSellingAgentMlsId.value !==
            liveValues?.Property?.CO_BUYER_AGENT_MLS_ID;

        // The Publish API will execute a save operation internally on the backend
        await publishListing();

        // Create a helper function for field access in the confirmation component
        const getResourceFieldValue = (
          resourceType: ListingResource,
          fieldName: string,
          fallbackValue: any,
        ) => {
          // Use the store directly for read access
          const state = useListingStore.getState();
          const value = state.getResourceFieldValue(
            resourceType,
            fieldName,
            fallbackValue,
          );
          return value;
        };

        // Helper to get media by category
        const getMediaByCategory = (...categories: MediaCategory[]) => {
          const state = useListingStore.getState();
          const allMedia = state[ListingResource.Media] as any[];
          return allMedia.filter((media: any) =>
            categories.includes(media.MEDIA_CATEGORY as MediaCategory),
          );
        };

        showPanel(
          'Published Successfully',
          <PublishConfirmation
            getResourceValue={getResourceFieldValue}
            getMediaByCategory={getMediaByCategory}
            onListingsClick={() => navigateTo(getOverviewUrl())}
          />,
          null,
          375,
          () => navigateTo(getOverviewUrl()),
        );

        // Capture analytics event using the field values we already have
        captureEvent(ApplicationEvent.ListingPublish, {
          listingKey: listingKey.value,
          listingId: listingId.value,
          propertyType: propertyType.value,
          listPrice: listPrice.value,
          address: address.value,
          mediaCount: mediaCount,
        });

        maybeSendSellingAgentEmail(
          isSellingAgentUpdated,
          isCoSellingAgentUpdated,
        );
      } catch (errorDetails: any) {
        // On failure, reset the publishing state
        isPublishing.updateValue(false);
        const error: any = isServiceError(errorDetails)
          ? errorDetails
          : {
              error: 'Error publishing listing',
              details: errorDetails.message,
            };

        if (errorDetails.message.includes('PUBLISH_IN_PROGRESS')) {
          error.message = errorDetails.message.split(':')[1];
          // set published to true to turn off autosave
          useListingStore.setState({
            published: true,
          });
          showPanel(
            'Publish Error',
            <PublishError
              error={error}
              onEditListing={() => navigateTo(window.location.href)}
            />,
            null,
            375,
            () => navigateTo(window.location.href),
          );
        } else {
          showPanel(
            'Publish Error',
            <PublishError error={error} onEditListing={hidePanel} />,
          );
        }
      } finally {
        loading(false);
      }
    };

    // Helper to get field value for the ListingSummary component
    const getFieldValue = (fieldName: PropertyField, fallbackValue: any) => {
      const state = useListingStore.getState();
      return state.getResourceFieldValue(
        ListingResource.Property,
        fieldName,
        fallbackValue,
      );
    };

    showPanel(
      'Publish Confirmation',
      <ListingSummary onSubmit={submit} getFieldValue={getFieldValue} />,
      undefined,
      undefined,
      () => {
        console.error('Publish Confirmation Cancelled');
        isPublishing.updateValue(false);
      },
    );
  };

  const exit = async (hasErrors: boolean, isFromLiveListing: boolean) => {
    if (isFromLiveListing) {
      showModal({
        title: "You're not done yet...",
        width: 'lg',
        content: (
          <BackHandlerContent
            publish={publish}
            hasErrors={hasErrors}
            discardListing={discardListing}
            saveListing={saveListing}
            onClose={() => {
              hideModal();
            }}
          />
        ),
      });
    } else {
      isNavigatingAway.updateValue(true);

      await saveListing();

      navigateTo(getOverviewUrl());
    }
  };

  const contextValue: EditListingContextType = {
    exit,
    publish,
  };

  return (
    <EditListingContext.Provider value={contextValue}>
      {children}
      {isMediaSyncEnabled && (
        <MediaSyncDialog
          open={showMediaSyncDialog}
          onClose={() => !isMediaSyncing && setShowMediaSyncDialog(false)}
          onSyncMedia={syncMedia}
          isSyncing={isMediaSyncing}
        />
      )}
    </EditListingContext.Provider>
  );
};

const isServiceError = (error: any): boolean => {
  return error && typeof error === 'object' && 'details' in error;
};

export const useEditListing = () => {
  const context = useContext(EditListingContext);
  if (context === undefined) {
    throw new Error(
      'useEditListing must be used within an EditListingProvider',
    );
  }
  return context;
};

export const EditListing: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const metadata = useMetadata();
  const { saveListing, publishListing } = useService();
  const { hasPermission } = useAuth();

  // Hook for publishing status
  const isPublishing = useListingStatus<boolean>('isPublishing', false);

  const handleSave = useCallback(
    async (listing: any, fromPublishStep: boolean = false) => {
      if (!fromPublishStep && isPublishing.value) {
        console.error('Cannot save while publishing');

        return {
          ListingKey: listing?.Property?.LISTING_KEY,
          ListingId: listing?.Property?.LISTING_ID,
        };
      }

      return await saveListing(listing.key ? 'UPDATE' : 'CREATE', listing.key, {
        Property: listing.Property,
        OpenHouse: listing.OpenHouse,
        Media: listing.Media,
        Unit: listing.Unit,
        Room: listing.Room,
        GreenVerification: listing.GreenVerification,
      });
    },
    [isPublishing.value, saveListing],
  );

  const handlePublish = async (listing: any) => {
    await publishListing(listing.key, {
      Property: listing.Property,
      OpenHouse: listing.OpenHouse,
      Media: listing.Media,
      Unit: listing.Unit,
      Room: listing.Room,
      GreenVerification: listing.GreenVerification,
    });
  };

  const handleDiscard = async (listing: any) => {
    await saveListing('DELETE', listing.key, {
      Property: listing.Property,
      OpenHouse: listing.OpenHouse,
      Media: listing.Media,
      Unit: listing.Unit,
      Room: listing.Room,
      GreenVerification: listing.GreenVerification,
    });
  };

  return (
    <ListingProvider
      onSave={handleSave}
      onPublish={handlePublish}
      onDiscard={handleDiscard}
      rules={metadata?.rules() || []}
      validationContext={{
        hasPermission: hasPermission, //TODO: Replace me
        locale: metadata?.locale(),
      }}
    >
      <ListingInactivityWarning />
      <EditListingProvider>{children}</EditListingProvider>
    </ListingProvider>
  );
};
